import { useEffect, useRef } from 'react'
import { useAppDispatch } from '@/store/hooks'
import { api } from '@/store/api'
import { ALL_LIST_TAGS } from '@/features/instructions/instructionsApi.tags'
import { useGetArticleModerationQuery } from './articleModerationApi'
import { useRefreshOnFocus } from '@/lib/useRefreshOnFocus'
import { isLiveModeration, pollingFor } from './lib/pollingFor'
import { useArticleModeration } from './useArticleModeration'
import { useFreshArticleModeration } from './useFreshArticleModeration'
import type { ReviewStatus } from './types'

/**
 * ЕДИНСТВЕННАЯ подписка на состояние модерации статьи. Остановка — `pollingInterval: 0`,
 * не `skip`. На переходе `pending → терминальный` статья и списки перечитываются:
 * одобрение меняет живую статью и `accessType`. Упала первая загрузка — повтор раз в
 * `MODERATION_ERROR_RETRY_MS`. Живая статья вне проверки опрашивается раз в `DRAFT_POLL_MS`
 * и на возврате во вкладку: черновик мог завести MCP или вторая вкладка, а засев формы
 * читает `draft` модерации — новый черновик вливается `rebaseArticleForm`. Статью сам
 * опрос НЕ перечитывает: итоговый засев после сохранения (`useSeedReadAt`) требует,
 * чтобы ОБА запроса ушли после `PATCH`, и тик одной модерации его не подделает.
 */
export const ArticleModerationPoller = ({ id }: { id: number }) => {
  const dispatch = useAppDispatch()
  const { state } = useArticleModeration(id)
  // «Первая загрузка упала» — по СВЕЖЕМУ ответу (как у курса): при устаревшем кеше упавший
  // запрос на монтировании иначе выглядел бы успешным (данные есть), и повтора не было бы.
  const fresh = useFreshArticleModeration(id)
  const status = state?.review?.status
  const isLive = isLiveModeration(state)
  const { refetch } = useGetArticleModerationQuery(id, {
    pollingInterval: pollingFor(status, fresh.isError && fresh.state === undefined, isLive),
    refetchOnMountOrArgChange: true,
  })
  useRefreshOnFocus(isLive, refetch)

  // Ключ по id: смена статьи без перемонтирования не должна давать ложного перехода.
  const prev = useRef<{ id: number; status: ReviewStatus | undefined } | null>(null)
  useEffect(() => {
    const before = prev.current?.id === id ? prev.current.status : undefined
    if (before === 'pending' && status !== undefined && status !== 'pending') {
      dispatch(api.util.invalidateTags([{ type: 'Instruction', id }, ...ALL_LIST_TAGS]))
    }
    prev.current = { id, status }
  }, [status, id, dispatch])

  return null
}
